import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { DISCLAIMER_TEXT } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LangContext";
import VendorMap from "@/components/VendorMap";
import VideoEmbed from "@/components/VideoEmbed";
import FramedImage from "@/components/FramedImage";
import { openNowState } from "@/lib/openNow";
import {
  Star,
  MapPin,
  Phone,
  Globe,
  Mail,
  Clock,
  Sparkles,
  MessageCircle,
  ShieldCheck,
  Flag,
  Plus,
} from "lucide-react";
import Icon from "@/components/Icon";
import VendorReviews from "@/components/VendorReviews";
import GalleryLightbox from "@/components/GalleryLightbox";
import ReportImageDialog from "@/components/ReportImageDialog";

export default function VendorProfile() {
  const { vendorId } = useParams();
  const [v, setV] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const [imageReport, setImageReport] = useState(null); // { url }
  const navigate = useNavigate();
  const { user } = useAuth();
  const { add: addToCart } = useCart() || {};
  const { t } = useLang();
  const [params] = useSearchParams();
  const wantChat = params.get("chat") === "1";

  useEffect(() => {
    api.get(`/vendors/${vendorId}`).then((r) => setV(r.data));
  }, [vendorId]);

  useEffect(() => {
    if (wantChat && v && user) startChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantChat, v?.vendor_id, user?.user_id]);

  const startChat = async () => {
    if (!user) return navigate("/login");
    try {
      const { data } = await api.post("/chats", { vendor_id: v.vendor_id });
      navigate(`/chat/${data.chat_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start chat");
    }
  };

  const submitReport = async () => {
    try {
      await api.post(`/vendors/${vendorId}/report`, { reason });
      toast.success(t("vp.report_success"));
      setReportOpen(false);
      setReason("");
    } catch (e) {
      toast.error("Could not submit report");
    }
  };

  const handleAddToCart = async (p) => {
    if (!user) return navigate("/login");
    if (!addToCart) return;
    try {
      await addToCart(v.vendor_id, p.name, 1, "");
      toast.success(t("vp.added_to_cart").replace("{name}", p.name));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to cart");
    }
  };

  if (!v)
    return (
      <div className="mx-auto max-w-5xl px-4 py-20 text-center text-muted-foreground">{t("vp.loading")}</div>
    );

  const isGuest = !user;

  return (
    <div className="relative">
      {/* Members-only gate — non-authenticated visitors see a blurred
          preview + a fixed CTA modal encouraging them to join. */}
      {isGuest && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-10 bg-background/60 backdrop-blur-[2px]"
          data-testid="vendor-members-only-gate"
        >
          <Card className="max-w-md w-full p-6 sm:p-8 border-primary/30 shadow-2xl bg-card/95 backdrop-blur-xl relative">
            <div className="flex items-center justify-center h-14 w-14 rounded-full bg-primary/10 mx-auto mb-4">
              <ShieldCheck className="h-7 w-7 text-primary" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-center mb-2">
              Members-only storefront
            </h2>
            <p className="text-sm text-muted-foreground text-center leading-relaxed mb-1">
              <strong className="text-foreground">{v.business_name}</strong> is listed on the
              closed sanskariBazarIn community.
            </p>
            <p className="text-xs text-muted-foreground text-center mb-6">
              Sign in with your invited email to browse this store, chat with the vendor, and
              place enquiries.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => navigate("/login", { state: { from: { pathname: `/vendors/${v.vendor_id}` } } })}
                className="w-full rounded-full h-11"
                data-testid="vendor-gate-login-btn"
              >
                Login to view store
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/register")}
                className="w-full rounded-full h-11"
                data-testid="vendor-gate-join-btn"
              >
                I have an invite code — Join now
              </Button>
              <button
                type="button"
                onClick={() => navigate("/")}
                className="text-[11px] text-muted-foreground hover:text-foreground mt-1"
                data-testid="vendor-gate-back-btn"
              >
                ← Back to home
              </button>
            </div>
          </Card>
        </div>
      )}

      <div
        className={isGuest ? "pointer-events-none select-none" : ""}
        style={isGuest ? { filter: "blur(6px)" } : undefined}
        aria-hidden={isGuest ? "true" : undefined}
      >
      {/* Banner — full image visible (letterbox with blurred fill so portrait / off-ratio banners aren't cropped) */}
      <div className="relative h-56 md:h-80 w-full overflow-hidden bg-muted">
        {v.banner && (
          <>
            <img
              src={v.banner}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover scale-110 blur-2xl opacity-70"
            />
            <img
              src={v.banner}
              alt={v.business_name}
              onError={(e) => {
                e.currentTarget.style.background = "linear-gradient(135deg,#f97316,#eab308,#16a34a)";
                e.currentTarget.removeAttribute("src");
              }}
              className="relative h-full w-full object-contain"
            />
          </>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent pointer-events-none" />
      </div>

      {/* Floating sticky chat CTA — always visible on vendor profile */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 items-end" data-testid="vendor-floating-actions">
        {v.whatsapp && (
          <Button
            asChild
            size="lg"
            className="rounded-full shadow-2xl h-14 px-5 gap-2 bg-green-600 hover:bg-green-700 text-white"
            data-testid="vendor-floating-whatsapp-btn"
            aria-label="WhatsApp"
          >
            <a
              href={`https://wa.me/${v.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(`Namaskar! I found ${v.business_name} on sanskariBazarIn and would like to enquire.`)}`}
              target="_blank"
              rel="noreferrer"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.16 5.335 5.495 0 12.05 0c3.181 0 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414 0 6.557-5.336 11.891-11.893 11.891-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.149-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.521.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
              <span className="hidden sm:inline font-semibold">WhatsApp</span>
            </a>
          </Button>
        )}
        <Button
          onClick={startChat}
          size="lg"
          className="rounded-full shadow-2xl shadow-primary/40 h-14 px-5 gap-2"
          data-testid="vendor-floating-chat-btn"
          aria-label="Chat with vendor"
        >
          <MessageCircle className="h-5 w-5" />
          <span className="hidden sm:inline font-semibold">{t("vp.chat_with_vendor")}</span>
        </Button>
      </div>


      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 -mt-14 pb-16">
        <div className="flex items-start gap-5 flex-wrap">
          <div className="h-24 w-24 md:h-28 md:w-28 rounded-2xl border-4 border-background overflow-hidden bg-card shadow-lg">
            {v.logo && (
              <img
                src={v.logo}
                alt=""
                onError={(e) => {
                  e.currentTarget.style.background = "linear-gradient(135deg,#f97316,#eab308)";
                  e.currentTarget.removeAttribute("src");
                }}
                className="h-full w-full object-cover"
              />
            )}
          </div>
          <div className="flex-1 min-w-0 pt-4">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter">{v.business_name}</h1>
              {v.is_verified && (
                <Badge className="bg-primary text-primary-foreground gap-1">
                  <ShieldCheck className="h-3 w-3" /> {t("vp.verified")}
                </Badge>
              )}
              {(() => {
                const live = openNowState(v.working_hours);
                const open = live === "open" || (live === "unknown" && v.is_open_now);
                return (
                  <Badge variant={open ? "default" : "secondary"} data-testid="vendor-open-badge">
                    {open ? t("vp.open_now") : t("vp.closed")}
                  </Badge>
                );
              })()}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {t("vp.by")} <strong>{v.owner_name}</strong> · {v.category}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" /> {v.city}</span>
              <span className="inline-flex items-center gap-1">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" /> {v.rating?.toFixed(1)} ({v.rating_count})
              </span>
              <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" /> {typeof v.working_hours === "string" ? v.working_hours : ""}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pt-4 w-full sm:w-auto">
            <Button onClick={startChat} size="lg" className="rounded-full flex-1 sm:flex-none" data-testid="vendor-profile-chat-btn">
              <MessageCircle className="h-4 w-4 mr-1.5" /> {t("vp.chat_with_vendor")}
            </Button>
            {v.whatsapp && (
              <Button asChild size="lg" variant="outline" className="rounded-full flex-1 sm:flex-none border-green-600 text-green-700 hover:bg-green-50 hover:text-green-700" data-testid="vendor-profile-whatsapp">
                <a
                  href={`https://wa.me/${v.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(`Namaskar! I found ${v.business_name} on sanskariBazarIn and would like to enquire.`)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4 mr-1.5 fill-current"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.16 5.335 5.495 0 12.05 0c3.181 0 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414 0 6.557-5.336 11.891-11.893 11.891-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.149-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.521.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
                  {t("vp.chat_on_whatsapp")}
                </a>
              </Button>
            )}
            <Dialog open={reportOpen} onOpenChange={setReportOpen}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="sm" className="text-muted-foreground" data-testid="vendor-profile-report-btn">
                  <Flag className="h-4 w-4 mr-1" /> {t("vp.report")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("vp.report_dialog_title")}</DialogTitle></DialogHeader>
                <Textarea
                  data-testid="report-reason-input"
                  placeholder={t("vp.report_placeholder")}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                <DialogFooter>
                  <Button variant="outline" onClick={() => setReportOpen(false)}>{t("vp.report_cancel")}</Button>
                  <Button onClick={submitReport} disabled={!reason.trim()} data-testid="report-submit-btn">{t("vp.report_submit")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {v.offer && (
          <Card className="p-4 mt-6 border-primary/40 bg-primary/5">
            <div className="flex items-center gap-2 text-sm">
              <Sparkles className="h-4 w-4 text-primary" />
              <strong>Offer:</strong> {v.offer}
            </div>
          </Card>
        )}

        <Tabs defaultValue="products" className="mt-8">
          <TabsList>
            <TabsTrigger value="products" data-testid="tab-products">{t("vp.tab_products")}</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery">{t("vp.tab_gallery")}</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">{t("vp.tab_reviews")}</TabsTrigger>
            <TabsTrigger value="contact" data-testid="tab-contact">{t("vp.tab_contact")}</TabsTrigger>
            <TabsTrigger value="about" data-testid="tab-about">{t("vp.tab_about")}</TabsTrigger>
            <TabsTrigger value="map" data-testid="tab-map">{t("vp.tab_map")}</TabsTrigger>
          </TabsList>
          <TabsContent value="about" className="mt-6">
            <Card className="p-6 border-border/60 space-y-4">
              <p className="text-muted-foreground leading-relaxed">{v.description}</p>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">{t("vp.service_area")}</span> {v.service_area}</div>
                <div><span className="text-muted-foreground">{t("vp.working_hours")}</span> {typeof v.working_hours === "string" ? v.working_hours : ""}</div>
              </div>
            </Card>
          </TabsContent>
          <TabsContent value="products" className="mt-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(v.products || []).map((p, i) => (
                <Card key={i} data-testid="product-card" className="overflow-hidden border-border/60 transition-all hover:-translate-y-1 hover:shadow-lg flex flex-col">
                  {(p.image || p.image_url) && <img src={p.image || p.image_url} alt={p.name} className="aspect-[4/3] w-full object-cover object-center" />}
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="flex items-center gap-2">
                      <div className="h-9 w-9 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
                        <Icon name={p.icon || "Tag"} className="h-4 w-4" />
                      </div>
                      <div className="font-semibold leading-tight">{p.name}</div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">{p.description}</div>
                    {p.price_range && (
                      <div className="text-sm mt-3">
                        <Badge variant="secondary">{p.price_range}</Badge>
                      </div>
                    )}
                    <div className="mt-auto pt-3 flex">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full w-full"
                        onClick={() => handleAddToCart(p)}
                        data-testid={`product-add-cart-${i}`}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" /> {t("vp.add_to_cart_short")}
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
              {(!v.products || v.products.length === 0) && (
                <p className="text-sm text-muted-foreground">{t("vp.no_products")}</p>
              )}
            </div>
          </TabsContent>
          <TabsContent value="gallery" className="mt-6 space-y-6">
            {(v.gallery_videos || []).filter(Boolean).length > 0 && (
              <div data-testid="vendor-gallery-videos">
                <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2">{t("vp.videos")}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(v.gallery_videos || []).filter(Boolean).map((u, i) => (
                    <VideoEmbed key={i} url={u} testId={`vendor-video-${i}`} />
                  ))}
                </div>
              </div>
            )}
            {(v.gallery || []).length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2">{t("vp.photos")}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {(v.gallery || []).map((g, i) => (
                    <div
                      key={i}
                      className="relative group"
                      data-testid={`vendor-gallery-tile-${i}`}
                    >
                      <FramedImage
                        src={g}
                        alt={`${v.business_name} — photo ${i + 1}`}
                        testId={`vendor-gallery-photo-${i}`}
                        onClick={() => setLightboxIdx(i)}
                      />
                      {user && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setImageReport({ url: g });
                          }}
                          data-testid={`vendor-gallery-report-${i}`}
                          title="Report this image"
                          className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/60 hover:bg-destructive text-white flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity z-10"
                        >
                          <Flag className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(v.gallery || []).length === 0 && (v.gallery_videos || []).filter(Boolean).length === 0 && (
              <p className="text-sm text-muted-foreground">{t("vp.no_gallery")}</p>
            )}
          </TabsContent>
          <TabsContent value="reviews" className="mt-6">
            <VendorReviews vendorId={v.vendor_id} onChange={(updated) => setV((x) => ({...x, rating: updated.rating, rating_count: updated.rating_count}))} />
          </TabsContent>
          <TabsContent value="contact" className="mt-6">
            <Card className="p-6 border-border/60 space-y-3 text-sm">
              {v.address && <div className="flex gap-2"><MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" /><span>{v.address}</span></div>}
              {v.phone && <div className="flex gap-2"><Phone className="h-4 w-4 mt-0.5 text-muted-foreground" /><a href={`tel:${v.phone}`} className="hover:underline">{v.phone}</a></div>}
              {v.email && <div className="flex gap-2"><Mail className="h-4 w-4 mt-0.5 text-muted-foreground" /><a href={`mailto:${v.email}`} className="hover:underline">{v.email}</a></div>}
              {v.website && <div className="flex gap-2"><Globe className="h-4 w-4 mt-0.5 text-muted-foreground" /><a href={v.website} target="_blank" rel="noreferrer" className="hover:underline">{v.website}</a></div>}
            </Card>
          </TabsContent>
          <TabsContent value="map" className="mt-6">
            <VendorMap vendors={[v]} height={420} />
            {v.loc?.source === "city" && (
              <p className="text-xs text-muted-foreground mt-2" data-testid="vendor-map-fallback-note">
                {t("vp.map_fallback").replace("{city}", v.city || "")}
              </p>
            )}
          </TabsContent>
        </Tabs>

        <Card className="p-5 mt-8 border-border/60 bg-muted/40">
          <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">{t("vp.disclaimer")}</div>
          <p className="text-sm sm:text-base text-foreground/80 leading-relaxed">
            {t("vp.disclaimer_text")}
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed mt-3">{DISCLAIMER_TEXT}</p>
        </Card>
      </div>
      </div>

      <GalleryLightbox
        images={v.gallery || []}
        index={lightboxIdx}
        onClose={() => setLightboxIdx(-1)}
        onIndex={setLightboxIdx}
        onReport={user ? (url) => { setLightboxIdx(-1); setImageReport({ url }); } : undefined}
      />

      <ReportImageDialog
        open={!!imageReport}
        onOpenChange={(o) => !o && setImageReport(null)}
        vendorId={v.vendor_id}
        vendorName={v.business_name}
        imageUrl={imageReport?.url}
      />
    </div>
  );
}
